import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';
import { MutableRefObject, useEffect, useRef, useState } from 'react';

type UseDropdownMenuOptions = {
  enabled?: boolean;
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';
};

type UseFloatingReturn = ReturnType<typeof useFloating>;
type UseInteractionsReturn = ReturnType<typeof useInteractions>;

type UseDropdownMenuReturn = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  activeIndex: number | null;
  refs: UseFloatingReturn['refs'];
  floatingStyles: UseFloatingReturn['floatingStyles'];
  context: UseFloatingReturn['context'];
  getReferenceProps: UseInteractionsReturn['getReferenceProps'];
  getFloatingProps: UseInteractionsReturn['getFloatingProps'];
  getItemProps: UseInteractionsReturn['getItemProps'];
  listRef: MutableRefObject<Array<HTMLButtonElement | null>>;
};

const useDropdownMenu = (
  options: UseDropdownMenuOptions = {}
): UseDropdownMenuReturn => {
  const { enabled = true, placement = 'bottom-start' } = options;

  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    if (!enabled) {
      setIsOpen(false);
    }
  }, [enabled]);

  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const listRef = useRef<Array<HTMLButtonElement | null>>([]);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open);
      if (!open) {
        setActiveIndex(null);
      }
    },
    placement,
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'menu' });
  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    loop: true,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [click, dismiss, role, listNavigation]
  );

  return {
    isOpen,
    setIsOpen,
    activeIndex,
    refs,
    floatingStyles,
    context,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
    listRef,
  };
};

export default useDropdownMenu;
